import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FilePlus2 } from "lucide-react";
import { auditKeys } from "@amber/access/ui";
import { collectionKeys } from "@amber/collection/ui";
import {
  Button,
  Callout,
  DataTable,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  LinkButton,
  ReasonField,
  SkeletonRows,
  Tag,
  TextField,
  apiPost,
  dataColumns,
  errorText,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useCsrf,
  useDebounced,
  useReason,
  useToast,
} from "@amber/ui";
import {
  POLICY_DIMENSION_KEYS,
  policyAppliedSchema,
  type PolicyDimensionKey,
  type PolicyDimensionsInput,
  type PolicyImpactView,
  type PolicyVersionView,
} from "../../contracts/index";
import { evidenceKeys } from "../keys";
import { policiesQuery, policyPreviewQuery } from "../queries";

type Rule = PolicyVersionView["rules"][number];

const col = dataColumns<Rule>();
const RULE_COLUMNS = [
  col.display({
    id: "rule",
    header: "Rule",
    meta: { width: 190, cellClassName: "font-strong text-fg" },
    cell: ({ row }) => row.original.label,
  }),
  col.display({
    id: "eligible",
    header: "To be Eligible",
    meta: { cellClassName: "whitespace-normal text-fg" },
    cell: ({ row }) => row.original.rule,
  }),
  col.display({
    id: "required",
    header: "Required",
    meta: { width: 120 },
    cell: ({ row }) => (row.original.required ? "Required" : <span className="text-muted">Not required</span>),
  }),
];

function RulesTable({ policy }: { policy: PolicyVersionView }) {
  return (
    <DataTable
      label={`${policy.name} version ${String(policy.version)} rules`}
      columns={RULE_COLUMNS}
      rows={policy.rules}
      rowId={(r) => r.dimension}
      minWidth={760}
      rowOptions={(r) => ({ titles: { eligible: r.rule } })}
      wrapClassName="flex-none rounded-b-xl border-b"
    />
  );
}

function impactText(impact: PolicyImpactView): string {
  if (impact.items === 0) return "Would change no item's readiness";
  const parts = [
    impact.eligible_to_draft > 0
      ? `${String(impact.eligible_to_draft)} ${impact.eligible_to_draft === 1 ? "item" : "items"} from Eligible to Draft`
      : null,
    impact.draft_to_eligible > 0
      ? `${String(impact.draft_to_eligible)} ${impact.draft_to_eligible === 1 ? "item" : "items"} from Draft to Eligible`
      : null,
  ].filter(Boolean);
  return `Would change ${parts.join(" and ")}`;
}

function ImpactPreview({ changed, impact }: { changed: boolean; impact: PolicyImpactView | undefined }) {
  if (!changed) return "Change a rule to see its impact.";
  if (!impact) return "Working out the impact…";
  return (
    <>
      <b className="font-strong">{impactText(impact)}</b>
      <span className="block text-muted">
        {String(impact.communications)} {impact.communications === 1 ? "communication" : "communications"} reassessed
        with a different result. Consumers reading the change feed get a reassessment notice for each.
      </span>
    </>
  );
}

function isRequired(d: PolicyDimensionsInput, k: PolicyDimensionKey): boolean {
  if (k === "language") return d.language.required;
  if (k === "attribution") return d.attribution.required;
  return d[k] === "required";
}

function setRequired(d: PolicyDimensionsInput, k: PolicyDimensionKey, on: boolean): PolicyDimensionsInput {
  if (k === "language") return { ...d, language: { ...d.language, required: on } };
  if (k === "attribution") return { ...d, attribution: { ...d.attribution, required: on } };
  return { ...d, [k]: on ? "required" : "not_required" };
}

function NewVersionDialog({ current, onClose }: { current: PolicyVersionView; onClose: () => void }) {
  const toast = useToast();
  const csrf = useCsrf();
  const [draft, setDraft] = useState<PolicyDimensionsInput>(current.dimensions);
  const [floor, setFloor] = useState(current.dimensions.attribution.min_unit_attribution_confidence.toFixed(2));
  const reason = useReason();
  const floorValue = Number(floor);
  const floorError =
    floor.trim() === "" || Number.isNaN(floorValue) || floorValue < 0 || floorValue > 1
      ? "Enter a confidence from 0 to 1"
      : null;
  const dimensions: PolicyDimensionsInput = floorError
    ? draft
    : { ...draft, attribution: { ...draft.attribution, min_unit_attribution_confidence: floorValue } };
  const debounced = useDebounced(dimensions, 250);
  const changed = JSON.stringify(dimensions) !== JSON.stringify(current.dimensions);
  const preview = useQuery(policyPreviewQuery(debounced, changed, csrf));
  const mutation = useCommandMutation({
    run: (token) =>
      apiPost(
        "/api/evidence/policies",
        { dimensions, reason: reason.trimmed, base_policy_id: current.id },
        policyAppliedSchema,
        token,
      ),
    invalidate: [evidenceKeys.all, collectionKeys.all, auditKeys.all],
    onSuccess: (result) => {
      toast.show(
        `${current.name} version ${String(result.version)} applied · ${impactText(result.impact).replace("Would change", "changed")}`,
      );
      onClose();
    },
  });
  const conflict = isApiError(mutation.error) && mutation.error.code === "conflict";
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`New version of ${current.name}`}
      onSubmit={() => {
        if (reason.submit() && changed && floorError === null) mutation.mutate();
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm={`Apply version ${String(current.version + 1)}`}
          pending={mutation.isPending}
        />
      }
    >
      <DialogText>
        Existing results are reassessed from stored check results; nothing is downloaded or transcribed again. Version{" "}
        {String(current.version)} stays readable.
      </DialogText>
      <fieldset className="m-0 mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-divider px-3 py-2.5 max-narrow:grid-cols-1">
        <legend className="px-1 text-md font-strong">Required rules</legend>
        {POLICY_DIMENSION_KEYS.map((k) => {
          const rule = current.rules.find((r) => r.dimension === k);
          return (
            <label key={k} className="flex min-h-6 items-center gap-2 text-md">
              <input
                type="checkbox"
                className="accent-[var(--color-link)]"
                checked={isRequired(draft, k)}
                onChange={(e) => {
                  setDraft(setRequired(draft, k, e.target.checked));
                }}
              />
              {rule?.label ?? k}
            </label>
          );
        })}
      </fieldset>
      <TextField
        label="Attribution confidence floor"
        required
        inputMode="decimal"
        value={floor}
        error={floorError}
        hint="Turns attributed to a tracked person below this stay Draft unless a person confirmed them"
        onChange={(e) => {
          setFloor(e.target.value);
        }}
      />
      <div role="status" className="mt-3 rounded-lg bg-surface-2 px-3 py-2 text-md">
        <ImpactPreview changed={changed} impact={preview.data} />
      </div>
      {reason.submitted && !changed ? (
        <Callout tone="attention" role="alert" className="mt-3">
          Change at least one rule
        </Callout>
      ) : null}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      {conflict ? (
        <Callout tone="attention" role="alert" className="mt-3" title="Another Admin applied a version meanwhile">
          Close this dialog to see the current version; your draft is not saved.
        </Callout>
      ) : (
        <FailedCallout error={mutation.error} />
      )}
    </Dialog>
  );
}

export function EvidencePolicies() {
  const query = useQuery(policiesQuery);
  const [viewing, setViewing] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const data = query.data;
  const action = data?.actions.new_version;
  const shown = data ? (data.versions.find((v) => v.version === viewing) ?? data.applied) : null;
  const old = shown && data && shown.id !== data.applied.id;
  return (
    <div className="mt-1">
      <div className="mx-4 mb-1 flex items-baseline gap-2 max-compact:mx-2">
        <h2 className="m-0 text-lg font-semibold">Evidence-use policy</h2>
        <span className="text-md text-muted">named and versioned; previous versions stay readable</span>
      </div>
      <p className="mx-4 mt-0 mb-2 max-w-[920px] text-md text-muted max-compact:mx-2">
        The policy decides when evidence is Eligible. The rules apply per tracked person in a communication: if any rule
        fails, that person&apos;s contributions are Draft, with the first failing rule as the reason. A communication is
        Eligible when at least one tracked person&apos;s contributions are, an item when at least one communication is.
      </p>
      {query.isError ? (
        <Callout tone="attention" role="alert" className="mx-4">
          Couldn&apos;t load the policies. {errorText(query.error)}
        </Callout>
      ) : null}
      {query.isPending ? (
        <div className="mx-4">
          <SkeletonRows columns={3} rows={3} />
        </div>
      ) : null}
      {shown && data ? (
        <div className="mx-4 rounded-xl border border-divider bg-bg max-compact:mx-2">
          <div className="flex flex-wrap items-center gap-2 border-b border-divider px-3 py-2">
            <b className="font-semibold">{shown.name}</b>
            <span className="font-mono text-md text-muted">v{String(shown.version)}</span>
            {shown.provisional ? <Tag tone="waiting">Provisional · not validated</Tag> : null}
            {old ? (
              <Tag tone="quiet">Previous version · read-only</Tag>
            ) : (
              <Tag tone="accent">Used for “Eligible” in this workspace</Tag>
            )}
            <span className="flex-1" />
            {old ? (
              <LinkButton
                onClick={() => {
                  setViewing(null);
                }}
              >
                Back to current version
              </LinkButton>
            ) : (
              <Button
                size="sm"
                disabledReason={action && !action.enabled ? action.reason : null}
                onClick={() => {
                  setCreating(true);
                }}
              >
                <FilePlus2 aria-hidden="true" />
                New version…
              </Button>
            )}
          </div>
          <RulesTable policy={shown} />
          <ul className="m-0 my-2 list-disc pr-3 pl-8 text-md text-muted">
            {data.expected_outcomes.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-divider px-3 py-2 text-md">
            <span className="text-muted">Versions:</span>
            {data.versions.map((v) => (
              <LinkButton
                key={v.id}
                aria-current={v.id === shown.id ? "true" : undefined}
                onClick={() => {
                  setViewing(v.state === "applied" ? null : v.version);
                }}
              >
                v{String(v.version)}
                {v.state === "applied" ? " (current)" : ""}
              </LinkButton>
            ))}
            <span className="text-muted">
              · v{String(shown.version)} by {shown.applied_by}, {formatDateTime(shown.applied_at)}: {shown.reason}
            </span>
          </div>
        </div>
      ) : null}
      {creating && data ? (
        <NewVersionDialog
          current={data.applied}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </div>
  );
}
